import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";

import { updateOrganizationApi } from "@/api/services/main/organization";
import { IconSave } from "@/assets/icons";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { organizationSchema } from "@/features/organization/schema/organizationSchema";
import { TOrganizationForm } from "@/features/organization/types/OrganizationForm";
import { convertFormDataToReqDto } from "@/features/organization/utils/organizationConverts";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { Button } from "@/modules/irohana-components/elements/Button";
import { CityInput } from "@/modules/irohana-components/elements/CityInput";
import { FooterActionBar } from "@/modules/irohana-components/elements/FooterActionBar";
import { Form } from "@/modules/irohana-components/elements/Form";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { InnerBox } from "@/modules/irohana-components/elements/InnerBox";
import { Input } from "@/modules/irohana-components/elements/Input";
import { Textarea } from "@/modules/irohana-components/elements/Textarea";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";

type TOrganizationFormProps = {
  defaultValues: TOrganizationForm;
};

export const OrganizationForm = ({ defaultValues }: TOrganizationFormProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState<string | null>(null);
  const methods = useForm<TOrganizationForm>({
    defaultValues,
    resolver: yupResolver(organizationSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control } = methods;

  // Entering 郵便番号 auto-fills 都道府県・市区町村 only (町名丁目番地号等 stays manual per spec).
  const locationInfo = useLocationInfo({
    methods,
    names: { zipCode: "zipCode", province: "province", municipality: "municipality" },
  });

  const onSubmit = async (data: TOrganizationForm) => {
    try {
      const payload = convertFormDataToReqDto(data);
      const rs = await updateOrganizationApi(payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL] });
      methods.reset(data);
      setBlockerEnabled(false);
      setRedirect(AppRoutes.organizationBusiness);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(redirect);
      setRedirect(null);
    }
  }, [blockerEnabled, redirect, navigate]);

  // Cancel / browser-back / reload trigger the shared unsaved-changes confirm modal.
  // Dirty tracking lives inside the Form element (React Hook Form dirtyFields), so any
  // interaction that touches a field — including click + blur that re-normalizes its
  // value — counts as dirty and blocks navigation.
  return (
    <Form methods={methods} onSubmit={onSubmit} schema={organizationSchema} enableBlocker={blockerEnabled}>
      <Stack gap={4}>
        <InnerBox title="組織情報">
          <Grid container columnSpacing={3} rowSpacing={2} mt={-1}>
            <Grid item xs={12}>
              <FormField control={control} name="name" label="組織名" required>
                <Input />
              </FormField>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormField control={control} name="zipCode" label="郵便番号">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormField control={control} name="province" label="都道府県">
                <Input readonly />
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CityInput name="municipality" control={control} zipCodeFieldName="zipCode" locationInfo={locationInfo} label="市区町村" />
            </Grid>

            <Grid item xs={12}>
              <FormField control={control} name="addressLine" label="町名丁目番地号等">
                <Input />
              </FormField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormField control={control} name="phone" label="電話番号">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField control={control} name="invoiceRegistrationNumber" label="インボイス登録番号">
                <Input />
              </FormField>
            </Grid>

            <Grid item xs={12}>
              <FormField control={control} name="bankAccount" label="振込先口座情報" required>
                <Textarea minRows={3} maxRows={6} />
              </FormField>
            </Grid>
          </Grid>
        </InnerBox>

        <FooterActionBar>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="ghost" onClick={() => navigate(AppRoutes.organizationBusiness)}>
              キャンセル
            </Button>
            <Button type="submit" variant="primary" size="sm" startIcon={<IconSave />} sx={{ fontWeight: 500 }}>
              保存
            </Button>
          </Stack>
        </FooterActionBar>
      </Stack>
    </Form>
  );
};
